import { strToU8, zlibSync, zipSync } from 'fflate'
import type { PixelData } from 'ag-psd'

export interface PngMetadata {
  iccProfile?: Uint8Array
  profileName?: string
  resolutionPpi?: number
}

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])
const CRC_TABLE = new Uint32Array(256)

for (let n = 0; n < 256; n += 1) {
  let value = n
  for (let bit = 0; bit < 8; bit += 1) {
    value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
  }
  CRC_TABLE[n] = value >>> 0
}

function writeU32(target: Uint8Array, offset: number, value: number) {
  target[offset] = (value >>> 24) & 0xff
  target[offset + 1] = (value >>> 16) & 0xff
  target[offset + 2] = (value >>> 8) & 0xff
  target[offset + 3] = value & 0xff
}

function crc32(data: Uint8Array) {
  let crc = 0xffffffff
  for (const byte of data) crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

function concat(parts: Uint8Array[]) {
  const total = parts.reduce((sum, part) => sum + part.length, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const part of parts) {
    result.set(part, offset)
    offset += part.length
  }
  return result
}

function chunk(type: string, data = new Uint8Array()) {
  const typeBytes = strToU8(type)
  const result = new Uint8Array(12 + data.length)
  writeU32(result, 0, data.length)
  result.set(typeBytes, 4)
  result.set(data, 8)
  writeU32(result, 8 + data.length, crc32(concat([typeBytes, data])))
  return result
}

function sanitizeProfileName(value: string) {
  const normalized = value.replace(/[^\x20-\x7e]/g, '').trim().slice(0, 79)
  return normalized || 'Embedded ICC profile'
}

function createIccChunk(profile: Uint8Array, name: string) {
  const profileName = strToU8(sanitizeProfileName(name))
  return chunk('iCCP', concat([profileName, new Uint8Array([0, 0]), zlibSync(profile, { level: 9 })]))
}

function createResolutionChunk(ppi: number) {
  const pixelsPerMeter = Math.max(1, Math.round(ppi / 0.0254))
  const data = new Uint8Array(9)
  writeU32(data, 0, pixelsPerMeter)
  writeU32(data, 4, pixelsPerMeter)
  data[8] = 1
  return chunk('pHYs', data)
}

function createRawScanlines(image: PixelData) {
  const is16Bit = image.data instanceof Uint16Array
  if (image.data instanceof Float32Array) {
    throw new Error('32 bit/channel のPNG書き出しはブラウザ版では未対応です。')
  }

  const bytesPerSample = is16Bit ? 2 : 1
  const stride = image.width * 4 * bytesPerSample
  const raw = new Uint8Array((stride + 1) * image.height)

  for (let y = 0; y < image.height; y += 1) {
    const rowStart = y * (stride + 1)
    raw[rowStart] = 0
    if (is16Bit) {
      const source = image.data as Uint16Array
      const sourceStart = y * image.width * 4
      for (let x = 0; x < image.width * 4; x += 1) {
        const sample = source[sourceStart + x]
        raw[rowStart + 1 + x * 2] = sample >>> 8
        raw[rowStart + 2 + x * 2] = sample & 0xff
      }
    } else {
      const source = image.data as Uint8Array | Uint8ClampedArray
      raw.set(source.subarray(y * image.width * 4, (y + 1) * image.width * 4), rowStart + 1)
    }
  }
  return raw
}

export function encodePng(image: PixelData, metadata: PngMetadata = {}) {
  if (image.width <= 0 || image.height <= 0) throw new Error('空のレイヤーは書き出せません。')
  const bitDepth = image.data instanceof Uint16Array ? 16 : 8
  const header = new Uint8Array(13)
  writeU32(header, 0, image.width)
  writeU32(header, 4, image.height)
  header[8] = bitDepth
  header[9] = 6
  header[10] = 0
  header[11] = 0
  header[12] = 0

  const chunks = [chunk('IHDR', header)]
  if (metadata.iccProfile) {
    chunks.push(createIccChunk(metadata.iccProfile, metadata.profileName ?? 'PSD embedded profile'))
  } else {
    chunks.push(chunk('sRGB', new Uint8Array([0])))
  }
  if (metadata.resolutionPpi) chunks.push(createResolutionChunk(metadata.resolutionPpi))
  chunks.push(chunk('IDAT', zlibSync(createRawScanlines(image), { level: 6 })))
  chunks.push(chunk('IEND'))
  return concat([PNG_SIGNATURE, ...chunks])
}

export function createZip(files: Record<string, Uint8Array>) {
  return zipSync(files, { level: 6 })
}

export function downloadBytes(bytes: Uint8Array, filename: string, mimeType: string) {
  const blob = new Blob([bytes as BlobPart], { type: mimeType })
  downloadBlob(blob, filename)
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

export function safeFilename(name: string, fallback = 'layer') {
  const sanitized = [...name.normalize('NFC')]
    .map((character) => character.charCodeAt(0) < 32 || '\\/:*?"<>|'.includes(character) ? '_' : character)
    .join('')
  const safe = sanitized
    .replace(/[. ]+$/g, '')
    .trim()
  return safe || fallback
}
